<template>
  <div class="relative overflow-hidden pb-1/2 h-full">
    <iframe
      v-if="url"
      ref="iframe"
      :src="url"
      class="absolute w-full h-full top-0 left-0"
      @load="handleIframeLoad"
      @error="handleIframeError"
    />
    <article-skeleton-loader
      v-if="isLoading"
      class="absolute w-full h-full top-0 left-0"
    />
    <div
      v-if="showEmptyState"
      class="absolute w-full h-full top-0 left-0 flex justify-center items-center"
    >
      <p>{{ $t('PORTAL.IFRAME_ERROR') }}</p>
    </div>
  </div>
</template>

<script>
import ArticleSkeletonLoader from 'shared/components/ArticleSkeletonLoader.vue';

export default {
  name: 'IframeLoader',
  components: {
    ArticleSkeletonLoader,
  },
  props: {
    url: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isLoading: true,
      showEmptyState: !this.url,
      clickedUrl: null,
      iframeEl: null,
    };
  },
  mounted() {
    // Add click listener to the component element instead
    this.iframeEl = this.$refs.iframe;
    this.iframeEl.addEventListener('click', this.captureClickedLink);
  },
  beforeDestroy() {
    // Clean up component listener
    this.iframeEl.removeEventListener('click', this.captureClickedLink);

    // Cleanup iframe listener if we added one
    if (this.iframeEl && this.iframeEl.contentDocument) {
      try {
        this.iframeEl.contentDocument.removeEventListener(
          'click',
          this.captureClickedLink
        );
      } catch (e) {
        // Silently fail if we can't remove the listener
      }
    }
  },
  methods: {
    handleIframeLoad() {
      // Once loaded, the loading state is hidden
      this.isLoading = false;

      try {
        // Try to access iframe content - will fail if cross-origin restrictions apply
        const iframe = this.$el.querySelector('iframe');
        if (!iframe) {
          /* eslint-disable-next-line no-console */
          console.log('iframe not found');

          return;
        }

        this.iframeEl = iframe;

        // This will throw an error if cross-origin restrictions apply
        const canAccess = !!iframe.contentWindow.location.href;

        if (canAccess) {
          // If we can access the iframe content, add a click listener to its document
          try {
            this.iframeEl.contentDocument.addEventListener(
              'click',
              this.captureClickedLink
            );
          } catch (e) {
            // Silently fail if we can't add the listener
          }
        }
      } catch (error) {
        /* eslint-disable-next-line no-console */
        console.log('Error loading iframe: ', error);
      }
    },
    handleIframeError() {
      // Hide the loading state and show the empty state when an error occurs
      this.isLoading = false;
      this.showEmptyState = true;
    },
    captureClickedLink(event) {
      // Capture the URL from the clicked link and open it directly
      if (event.target.tagName === 'A' || event.target.closest('a')) {
        const link =
          event.target.tagName === 'A'
            ? event.target
            : event.target.closest('a');
        this.clickedUrl = link.href;

        // Check if the link is internal or has /hc/ path
        const isInternalLink = this.isInternalOrHCLink(this.clickedUrl);

        // Allow normal navigation for internal links or links with /hc/ path
        if (isInternalLink) {
          return;
        }

        // Open external links in a new tab
        window.open(this.clickedUrl, '_blank');

        // Prevent default behavior
        event.preventDefault();
      }
    },
    isInternalOrHCLink(url) {
      try {
        // Parse the URL
        const linkUrl = new URL(url);
        const currentUrl = new URL(window.location.href);

        // Check if it's from the same origin (our app)
        const isSameOrigin = linkUrl.origin === currentUrl.origin;

        // Check if the URL contains the /hc/ path
        const isHelpCenterPath = linkUrl.pathname.includes('/hc/');

        return isSameOrigin || isHelpCenterPath;
      } catch (error) {
        // If URL parsing fails, assume it's a relative URL (internal)
        return true;
      }
    },
  },
};
</script>
